import { applyVerifiedEventMetadata } from './verifiedEventMetadata.js';
const FINAL_PATTERN = /\b(final|complete(?:d)?|match over)\b/i;
const LIVE_PATTERN = /\b(live|in progress|in-progress|playing|set\s*[1-5]|period\s*[1-5])\b/i;
const SPECIAL_STATES = [
  ['canceled', /\b(cancelled|canceled)\b/i, 'Canceled'],
  ['postponed', /\bpostponed\b/i, 'Postponed'],
  ['suspended', /\bsuspended\b/i, 'Suspended'],
  ['rescheduled', /\brescheduled\b/i, 'Rescheduled'],
  ['unconfirmed', /\b(?:unconfirmed|status unknown)\b/i, 'Status unconfirmed'],
];

function text(...values) {
  return values
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map(String)
    .join(' ')
    .trim();
}

function score(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function setScores(team) {
  if (!Array.isArray(team?.setScores)) return [];
  return team.setScores.slice(0, 5).map(score);
}

function teamKey(team) {
  return String(team?.canonicalId || team?.slug || team?.id || team?.name || '')
    .trim()
    .toLowerCase();
}

export function matchIdentity(game) {
  const id = String(game?.gameId || game?.id || '').trim();
  if (/^[1-9]\d*$/.test(id)) return `id:${id}`;
  const teams = [teamKey(game?.away), teamKey(game?.home)].filter(Boolean).sort();
  return teams.length === 2 ? `teams:${teams.join('|')}` : '';
}

export function isCompletedSet(away, home, setNumber = 1) {
  if (!Number.isFinite(away) || !Number.isFinite(home) || away === home) return false;
  const target = setNumber === 5 ? 15 : 25;
  return Math.max(away, home) >= target && Math.abs(away - home) >= 2;
}

function readCurrentSet(...values) {
  for (const value of values) {
    if (Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 5) {
      return Number(value);
    }

    const label = String(value || '').trim();
    const match =
      label.match(/(?:set|period)\s*([1-5])\b/i) ||
      label.match(/\b([1-5])(?:st|nd|rd|th)?\s*(?:set|period)\b/i);

    if (match) return Number(match[1]);
  }
  return null;
}

function summarizeSets(awayScores, homeScores) {
  let awayWins = 0;
  let homeWins = 0;
  let lastStartedSet = 0;
  let lastCompletedSet = 0;

  for (let index = 0; index < 5; index += 1) {
    const away = awayScores[index];
    const home = homeScores[index];
    if (Number.isFinite(away) || Number.isFinite(home)) lastStartedSet = index + 1;
    if (!isCompletedSet(away, home, index + 1)) continue;
    lastCompletedSet = index + 1;
    if (away > home) awayWins += 1;
    else homeWins += 1;
  }

  return { awayWins, homeWins, lastStartedSet, lastCompletedSet };
}

/**
 * Derive volleyball set state once for every consumer. A provider's set label is
 * treated as evidence, not as proof that a completed set is still active.
 */
export function deriveMatchSetState(game = {}) {
  game = applyVerifiedEventMetadata(game);
  const paused = specialState(game);
  const pair = normalizePair(game);
  const providerText = text(
    game?.rawStatus,
    game?.status,
    game?.statusLabel,
    game?.gameState,
    game?.currentPeriod
  );
  const explicitSet = readCurrentSet(
    game?.currentSet,
    game?.currentPeriod,
    game?.statusLabel,
    game?.rawStatus
  );
  const explicitFinal = FINAL_PATTERN.test(providerText) || game?.status === 'final';
  const explicitLive = LIVE_PATTERN.test(providerText) || game?.status === 'live';
  let awayWins = 0;
  let homeWins = 0;

  const sets = Array.from({ length: 5 }, (_, index) => {
    const number = index + 1;
    const away = pair.away[index] ?? null;
    const home = pair.home[index] ?? null;
    const hasScore = Number.isFinite(away) || Number.isFinite(home);
    const hasPoints = Number(away || 0) > 0 || Number(home || 0) > 0;
    const completed = isCompletedSet(away, home, number);

    if (completed) {
      if (away > home) awayWins += 1;
      else homeWins += 1;
    }

    return {
      number,
      away,
      home,
      hasScore,
      hasPoints,
      completed,
      winner: completed ? (away > home ? 'away' : 'home') : '',
      state: completed ? 'completed' : hasScore ? 'not-started' : 'not-started',
    };
  });

  [awayWins, homeWins] = resolvedWinPair(game, awayWins, homeWins);
  const matchCompleted = explicitFinal || awayWins >= 3 || homeWins >= 3;
  const latestIncomplete = [...sets]
    .reverse()
    .find((set) => !set.completed && (set.hasPoints || (explicitLive && explicitSet === set.number)));
  const explicitActive = explicitSet
    ? sets[explicitSet - 1]
    : null;
  const activeSet = matchCompleted || paused
    ? null
    : explicitActive && !explicitActive.completed
      ? Math.max(explicitActive.number, latestIncomplete?.number || 0)
      : latestIncomplete?.number || null;
  const lastCompletedSet = sets.reduce(
    (latest, set) => (set.completed ? Math.max(latest, set.number) : latest),
    0
  );
  const lastStartedSet = sets.reduce(
    (latest, set) => (set.completed || set.hasPoints || activeSet === set.number
      ? Math.max(latest, set.number)
      : latest),
    0
  );
  const betweenSets = !paused && !matchCompleted && explicitLive && !activeSet && lastCompletedSet > 0;
  const nextSet = betweenSets && lastCompletedSet < 5 ? lastCompletedSet + 1 : null;

  sets.forEach((set) => {
    if (set.completed) set.state = 'completed';
    else if (paused && set.hasPoints) set.state = 'paused';
    else if (activeSet === set.number) set.state = 'in-progress';
    else set.state = 'not-started';
  });

  return {
    sets,
    awayWins,
    homeWins,
    activeSet,
    currentSet: activeSet,
    nextSet,
    lastCompletedSet,
    lastStartedSet,
    betweenSets,
    matchCompleted,
    phase: paused ? 'paused' : matchCompleted
      ? 'match-completed'
      : activeSet
        ? 'set-in-progress'
        : betweenSets
          ? 'between-sets'
          : 'not-started',
  };
}

function specialState(game) {
  const value = text(game?.terminalState, game?.status, game?.statusLabel, game?.rawStatus);
  return SPECIAL_STATES.find(([, pattern]) => pattern.test(value)) || null;
}

function statusRank(value) {
  if (value === 'final') return 2;
  if (value === 'live') return 1;
  return 0;
}

function normalizePair(game) {
  const away = setScores(game?.away);
  const home = setScores(game?.home);
  const length = Math.max(away.length, home.length);
  // A verified final is stronger evidence than stale, contradictory set points.
  // Keep compatible partial linescores, but never use them to alter that result.
  const summary = summarizeSets(away, home);
  const result = [score(game?.away?.score), score(game?.home?.score)];
  if (game?.authoritative === true && FINAL_PATTERN.test(text(game.status, game.statusLabel)) &&
      validWinPair(...result) && Math.max(...result) === 3 &&
      (summary.awayWins > result[0] || summary.homeWins > result[1] ||
        summary.lastStartedSet > result[0] + result[1])) {
    return { away: Array(length).fill(null), home: Array(length).fill(null) };
  }
  return {
    away: Array.from({ length }, (_, index) => away[index] ?? null),
    home: Array.from({ length }, (_, index) => home[index] ?? null),
  };
}

function validWinPair(away, home) {
  return Number.isInteger(away) && Number.isInteger(home) &&
    away >= 0 && home >= 0 && away <= 3 && home <= 3 && away + home <= 5;
}

function resolvedWinPair(game, away, home) {
  const supplied = [score(game?.away?.score), score(game?.home?.score)];
  if (!validWinPair(...supplied)) return validWinPair(away, home) ? [away, home] : [0, 0];
  if (game?.authoritative === true && FINAL_PATTERN.test(text(game.status, game.statusLabel))) return supplied;
  // Results, like individual sets, are indivisible pairs. Prefer forward
  // progress, never independent maxima from mutually inconsistent snapshots.
  if (away >= supplied[0] && home >= supplied[1] && validWinPair(away, home)) return [away, home];
  return supplied;
}

export function normalizeMatchSnapshot(game = {}) {
  game = applyVerifiedEventMetadata(game);
  const pair = normalizePair(game);
  const setSummary = summarizeSets(pair.away, pair.home);
  const providerText = text(
    game?.rawStatus,
    game?.status,
    game?.statusLabel,
    game?.gameState,
    game?.currentPeriod
  );
  const special = specialState(game);
  const explicitFinal = FINAL_PATTERN.test(providerText) || game?.status === 'final';
  const explicitLive = LIVE_PATTERN.test(providerText) || game?.status === 'live';
  const suppliedAwayWins = score(game?.away?.score);
  const suppliedHomeWins = score(game?.home?.score);
  const preliminary = deriveMatchSetState({
    ...game,
    away: { ...(game?.away || {}), setScores: pair.away },
    home: { ...(game?.home || {}), setScores: pair.home },
  });
  const awayWins = preliminary.awayWins;
  const homeWins = preliminary.homeWins;
  const matchFinished = explicitFinal || preliminary.matchCompleted;
  const currentSet = matchFinished ? null : preliminary.activeSet;
  const scoringStarted = [...pair.away, ...pair.home].some((value) => value > 0) || awayWins > 0 || homeWins > 0;
  const status = special
    ? special[0]
    : matchFinished
      ? 'final'
      : explicitLive || scoringStarted
        ? 'live'
        : 'scheduled';
  const statusLabel = special
    ? special[2]
    : status === 'final'
      ? 'Final'
      : status === 'live'
        ? currentSet
          ? `Set ${currentSet}`
          : preliminary.betweenSets
            ? 'Between sets'
            : 'Live'
        : 'Upcoming';

  return {
    ...game,
    status,
    statusLabel,
    terminalState: special?.[0] || '',
    live: status === 'live',
    final: status === 'final',
    scheduled: status === 'scheduled',
    currentSet,
    nextSet: preliminary.nextSet,
    matchPhase: matchFinished ? 'match-completed' : preliminary.phase,
    currentPeriod: currentSet ? `Set ${currentSet}` : status === 'final' ? 'Final' : game?.currentPeriod || '',
    away: {
      ...(game?.away || {}),
      score: status === 'scheduled' ? null : setSummary.lastStartedSet > 0 ? awayWins : suppliedAwayWins,
      winner: status === 'final' && awayWins > homeWins,
      setScores: status === 'scheduled' ? [] : pair.away,
    },
    home: {
      ...(game?.home || {}),
      score: status === 'scheduled' ? null : setSummary.lastStartedSet > 0 ? homeWins : suppliedHomeWins,
      winner: status === 'final' && homeWins > awayWins,
      setScores: status === 'scheduled' ? [] : pair.home,
    },
  };
}

function orientIncoming(previous, incoming) {
  const previousAway = teamKey(previous?.away);
  const previousHome = teamKey(previous?.home);
  const incomingAway = teamKey(incoming?.away);
  const incomingHome = teamKey(incoming?.home);
  if (
    previousAway && previousHome && incomingAway && incomingHome &&
    previousAway === incomingHome && previousHome === incomingAway
  ) {
    return { ...incoming, away: incoming.home, home: incoming.away };
  }
  return incoming;
}

function snapshotTime(game) {
  const value = game?.sourceUpdatedAt || game?.updatedAt || game?.fetchedAt;
  const parsed = typeof value === 'number' ? value : Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : null;
}

function canCorrectSnapshot(previous, incoming) {
  if (incoming.authoritative !== true) return false;
  if (incoming.scoreSource === 'official-school-result' && previous.scoreSource !== incoming.scoreSource) return true;
  if (previous.authoritative !== true) return true;
  const nextTime = snapshotTime(incoming);
  const oldTime = snapshotTime(previous);
  return nextTime !== null ? oldTime === null || nextTime >= oldTime : oldTime === null;
}

function mergeTeamRecord(previous = {}, incoming = {}) {
  const oldDate = previous.recordThroughDate || previous.finalRecord?.throughDate || '';
  const newDate = incoming.recordThroughDate || incoming.finalRecord?.throughDate || '';
  if (previous.record && oldDate && (!newDate || oldDate > newDate)) {
    return { ...incoming, record: previous.record, recordThroughDate: oldDate,
      recordSource: previous.recordSource, finalRecord: previous.finalRecord };
  }
  return incoming;
}

function mergeSetPairs(previous, incoming) {
  const oldPair = normalizePair(previous);
  const newPair = normalizePair(incoming);
  const length = Math.max(oldPair.away.length, newPair.away.length, oldPair.home.length, newPair.home.length);
  const away = [];
  const home = [];

  for (let index = 0; index < length; index += 1) {
    const oldAway = oldPair.away[index];
    const oldHome = oldPair.home[index];
    const newAway = newPair.away[index];
    const newHome = newPair.home[index];
    const oldTotal = Number.isFinite(oldAway) && Number.isFinite(oldHome) ? oldAway + oldHome : -1;
    const newTotal = Number.isFinite(newAway) && Number.isFinite(newHome) ? newAway + newHome : -1;
    const oldCompleted = isCompletedSet(oldAway, oldHome, index + 1);
    const corrected = canCorrectSnapshot(previous, incoming);
    const useNew = newTotal >= 0 && (corrected || oldTotal < 0 ||
      (!oldCompleted && newAway >= oldAway && newHome >= oldHome));
    // A set is an indivisible pair. Never combine one side from each source.
    away[index] = useNew ? newAway : oldAway ?? null;
    home[index] = useNew ? newHome : oldHome ?? null;
  }
  return { away, home };
}

export function isStaleLiveFeed(game, now = Date.now()) {
  if (game?.status !== 'live') return false;
  const epoch = Number(game.startTimeEpoch);
  const start = epoch > 0 ? epoch * 1000 : Date.parse(game.startTimeIso || '');
  if (Number.isFinite(start)) return now - start > 8 * 60 * 60_000;
  const date = String(game.date || game.dateKey || game.scheduleDate || '').slice(0, 10);
  const end = Date.parse(date + 'T23:59:59-04:00');
  return Number.isFinite(end) && now - end > 8 * 60 * 60_000;
}

export function mergeMatchSnapshots(previous, candidate) {
  if (!previous) return normalizeMatchSnapshot(candidate || {});
  if (!candidate) return normalizeMatchSnapshot(previous);

  const oldMatch = normalizeMatchSnapshot(previous);
  let nextMatch = normalizeMatchSnapshot(orientIncoming(oldMatch, candidate));
  // A stale NCAA snapshot must not undo a verified school final. Accept NCAA
  // linescores again when its match result has caught up to the verified result.
  if (oldMatch.status === 'final' && oldMatch.scoreSource === 'official-school-result' &&
      nextMatch.scoreSource !== 'official-school-result' &&
      (nextMatch.status !== 'final' || nextMatch.away.score !== oldMatch.away.score ||
        nextMatch.home.score !== oldMatch.home.score)) {
    nextMatch = { ...nextMatch, status: 'final', statusLabel: 'Final',
      authoritative: true, scoreSource: oldMatch.scoreSource,
      sourceUpdatedAt: oldMatch.sourceUpdatedAt, sourceUrl: oldMatch.sourceUrl,
      away: { ...nextMatch.away, score: oldMatch.away.score, setScores: oldMatch.away.setScores },
      home: { ...nextMatch.home, score: oldMatch.home.score, setScores: oldMatch.home.setScores } };
  }
  const resultDiffers = oldMatch.away.score !== nextMatch.away.score || oldMatch.home.score !== nextMatch.home.score;
  const rejectFinalChange = oldMatch.status === 'final' && resultDiffers && !canCorrectSnapshot(oldMatch, nextMatch);
  const pair = mergeSetPairs(oldMatch, rejectFinalChange ? oldMatch : nextMatch);
  if (nextMatch.status === 'final' && nextMatch.scoreSource === 'official-school-result') {
    pair.away.forEach((away, i) => {
      if (!isCompletedSet(away, pair.home[i], i + 1)) {
        pair.away[i] = null;
        pair.home[i] = null;
      }
    });
  }
  const explicitSpecial = specialState(nextMatch);
  const preventRegression = !explicitSpecial && statusRank(nextMatch.status) < statusRank(oldMatch.status);
  const base = preventRegression
    ? { ...nextMatch, status: oldMatch.status, statusLabel: oldMatch.statusLabel, currentSet: oldMatch.currentSet }
    : nextMatch;

  const forwardResult = oldMatch.status !== 'final' && validWinPair(nextMatch.away.score, nextMatch.home.score) &&
    nextMatch.away.score >= (oldMatch.away.score ?? 0) && nextMatch.home.score >= (oldMatch.home.score ?? 0);
  const acceptResult = !preventRegression && !rejectFinalChange &&
    (canCorrectSnapshot(oldMatch, nextMatch) || forwardResult);
  if (rejectFinalChange) {
    base.scoreSource = oldMatch.scoreSource;
    base.authoritative = oldMatch.authoritative;
    base.sourceUpdatedAt = oldMatch.sourceUpdatedAt || oldMatch.updatedAt || oldMatch.fetchedAt;
  }
  if (oldMatch.scoreSource === 'official-school-result' && candidate.scoreSource !== 'official-school-result') {
    base.scoreSource = oldMatch.scoreSource;
    base.authoritative = true;
    base.sourceUpdatedAt = oldMatch.sourceUpdatedAt;
    base.sourceUrl = oldMatch.sourceUrl;
  }
  // Empty metadata from a summary is not evidence that known detail disappeared.
  ['venue', 'network', 'timeLabel', 'startTimeIso'].forEach((key) => {
    if (!base[key] && oldMatch[key]) base[key] = oldMatch[key];
  });

  return normalizeMatchSnapshot({
    ...oldMatch,
    ...base,
    away: {
      ...(oldMatch.away || {}),
      ...mergeTeamRecord(oldMatch.away, base.away),
      score: acceptResult ? base.away?.score : oldMatch.away?.score,
      setScores: pair.away,
    },
    home: {
      ...(oldMatch.home || {}),
      ...mergeTeamRecord(oldMatch.home, base.home),
      score: acceptResult ? base.home?.score : oldMatch.home?.score,
      setScores: pair.home,
    },
  });
}

export function mergeMatchCollections(previous = [], incoming = []) {
  const merged = new Map();
  previous.forEach((game) => {
    const identity = matchIdentity(game);
    if (identity) merged.set(identity, normalizeMatchSnapshot(game));
  });
  incoming.forEach((game) => {
    const identity = matchIdentity(game);
    if (!identity) return;
    merged.set(identity, mergeMatchSnapshots(merged.get(identity), game));
  });
  return Array.from(merged.values()).sort(
    (left, right) => Number(left?.startTimeEpoch || Number.MAX_SAFE_INTEGER) - Number(right?.startTimeEpoch || Number.MAX_SAFE_INTEGER)
  );
}

export function countMatchStatuses(games = []) {
  return games.reduce(
    (counts, game) => {
      const status = normalizeMatchSnapshot(game).status;
      const group = ['live', 'final', 'scheduled'].includes(status) ? status : 'other';
      counts[group] += 1;
      return counts;
    },
    { scheduled: 0, live: 0, final: 0, other: 0 }
  );
}
